import { inject, Service } from '@angular/core';
import { PreloadingStrategy, Route } from '@angular/router';
import { Observable, of } from 'rxjs';
import { mergeMap } from 'rxjs/operators';

import { PlatformService } from '@core/platform/platform.service';

/** The subset of the Network Information API this strategy reads (Chromium-only; optional everywhere else). */
interface NetworkInformationLike {
  readonly saveData?: boolean;
  readonly effectiveType?: 'slow-2g' | '2g' | '3g' | '4g';
}

interface NavigatorWithConnection extends Navigator {
  readonly connection?: NetworkInformationLike;
}

/**
 * PreloadStrategy — background-preloads ONLY the /v2 feature chunks (the routes
 * flagged `data: { preload: true }`), a beat after the page has ACTUALLY
 * finished loading and the main thread reports idle — never sooner, so it can
 * never compete with a route's own first-paint/LCP resources for the same
 * connection. This makes switching between the الرعاية / خويك / المجتمع / حسابي
 * tabs feel instant instead of waiting on a lazy-chunk download + parse on
 * first tap, without taxing the initial page load that every visitor pays for.
 *
 * Network-aware: skipped entirely on a `Save-Data` request or a `slow-2g`/`2g`
 * `effectiveType` (Network Information API, browser-support-dependent — simply
 * not gated on unsupported browsers) — a visitor on a genuinely slow or
 * metered connection shouldn't spend it on speculative future-tab downloads.
 *
 * (2026-09-05, rev. 3 perf audit — see docs/architecture/PERFORMANCE-AUDIT.md:
 * the previous version fired all flagged chunks off a flat 1200ms timer with
 * no `load`/idle gate. Measured against a fresh production build under
 * Lighthouse's mobile throttle, that timer elapsed WHILE the page's own
 * critical initial bundle was still in flight, adding ~1.5MB of unrelated-route
 * JS — `khoyek-page`, `profile-page`, … — to the same congested connection and
 * materially inflating FCP/LCP. Gating on `load` + idle removes that overlap
 * entirely: preloading now only ever starts after the visible page is done.)
 *
 * ISOLATION (the /v2 invariant): v1 routes carry no `preload` flag, so this
 * returns `of(null)` for them and they are NEVER preloaded — v1's existing
 * on-demand loading is byte-for-byte unchanged. (Angular's RouterPreloader still
 * WALKS the whole config and may create a route's `providers` EnvironmentInjector
 * early, but the v2 feature providers are plain lazy `makeEnvironmentProviders`
 * with no environment initializer — so no facade is constructed and no HTTP is
 * fired until the user actually activates the route.)
 *
 * SSR-safe: the RouterPreloader only runs in the browser after bootstrap
 * (never during SSR). `PlatformService` still gates every browser-only read
 * (`window`, `navigator.connection`) so this degrades to the plain fixed-delay
 * behavior in any environment without them (e.g. a unit-test `TestBed`).
 */
@Service()
export class PreloadStrategy implements PreloadingStrategy {
  /** Extra settle time after `load`+idle before the first flagged chunk fires. */
  private static readonly IDLE_MS = 2000;
  /** Upper bound if the browser never reports idle (rIC's own `timeout` option). */
  private static readonly IDLE_TIMEOUT_MS = 5000;

  private readonly platform = inject(PlatformService);

  preload(route: Route, load: () => Observable<unknown>): Observable<unknown> {
    if (!route.data?.['preload']) return of(null);
    if (this.isSlowConnection()) return of(null);
    return this.whenIdle().pipe(mergeMap(() => load()));
  }

  private isSlowConnection(): boolean {
    const nav = this.platform.window?.navigator as NavigatorWithConnection | undefined;
    const conn = nav?.connection;
    if (!conn) return false;
    return conn.saveData === true || conn.effectiveType === 'slow-2g' || conn.effectiveType === '2g';
  }

  /** Resolves once the window has fired `load` AND the main thread reports idle. */
  private whenIdle(): Observable<void> {
    return new Observable<void>((subscriber) => {
      const win = this.platform.window;
      if (!win) {
        // No window (SSR/test double) — never actually reached in production
        // (see the SSR-safe note above), but resolve rather than hang.
        subscriber.next();
        subscriber.complete();
        return undefined;
      }

      const fire = () => {
        subscriber.next();
        subscriber.complete();
      };
      const scheduleIdle = () => {
        const ric = win.requestIdleCallback?.bind(win);
        if (ric) {
          ric(() => win.setTimeout(fire, PreloadStrategy.IDLE_MS), {
            timeout: PreloadStrategy.IDLE_TIMEOUT_MS,
          });
        } else {
          win.setTimeout(fire, PreloadStrategy.IDLE_MS);
        }
      };

      if (win.document.readyState === 'complete') {
        scheduleIdle();
        return undefined;
      }
      win.addEventListener('load', scheduleIdle, { once: true });
      return () => win.removeEventListener('load', scheduleIdle);
    });
  }
}
